
import StripePayment from '@/services/payments/StripePayment';
import cartMixin from '@/mixins/cartMixin';
import addressMixin from '@/mixins/addressMixin';
import checkoutPaymentMixin from '@/mixins/checkoutPaymentMixin';
import { loadStripe } from '@stripe/stripe-js';
import { CarriersService } from '@/services/CarriersService.js';

export default {
  name: 'CheckoutPaymentFormExpress',
  mixins: [cartMixin, addressMixin, checkoutPaymentMixin],
  props: {
    paymentMethod: {
      type: Object,
      default: null,
    },
    paymentMethodTypes: {
      type: String,
      default: 'card,paypal',
    },
  },
  data() {
    return {
      stripe: null,
      elements: null,
      clientSecret: null,
      loading: true,
      expressCheckoutElement: null,
      shippingRatesById: {},
      pendingShippingAddress: null,
      pendingPayerName: '',
      pendingShippingRateId: null,
      initialContextPrepared: false,
      initializing: false,
      isProcessingSync: false,
      lastSyncedAddressHash: null,
      lastSyncedRateId: null,
      lastFetchedCartHash: null,
      carriersService: new CarriersService({
        http: this.$http,
        auth: this.$auth,
        store: this.$store,
      }),
      isEmpty: true,
    };
  },
  computed: {
    stripePayment() {
      return new StripePayment({
        cart: this.cart,
        auth: this.$auth,
        http: this.$http,
        addressService: this.addressService,
      });
    },
    canInitializeExpress() {
      return Boolean(
        this.stripe &&
          this.cart?.IdCart &&
          Number(this.cart?.Total?.ToPay?.TaxIncl || 0) > 0
      );
    },
  },
  mounted() {
    loadStripe(process.env.STRIPE_PUBLIC_KEY).then((stripe) => {
      this.stripe = stripe;
      this.maybeInitialize();
    });
  },
  beforeDestroy() {
    this.destroyExpressElement();
  },
  watch: {
    canInitializeExpress(val) {
      if (val && !this.expressCheckoutElement) {
        this.maybeInitialize();
      }
    },
  },
  methods: {
    toCents(value) {
      return Math.round(Number(value || 0) * 100);
    },

    /**
     * Extract the real carrier ID from a compound shipping rate ID.
     * e.g. "23_3500" → "23", "23" → "23"
     */
    extractCarrierId(rateId) {
      if (!rateId) return null;
      const str = String(rateId);
      const underscoreIdx = str.indexOf('_');
      return underscoreIdx > 0 ? str.substring(0, underscoreIdx) : str;
    },

    withTimeout(promise, timeoutMs = 3000) {
      let timer = null;
      const timeoutPromise = new Promise((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs);
      });

      return Promise.race([promise, timeoutPromise]).finally(() => {
        if (timer) clearTimeout(timer);
      });
    },

    destroyExpressElement() {
      if (!this.expressCheckoutElement) return;
      this.expressCheckoutElement.destroy();
      this.expressCheckoutElement = null;
    },

    async maybeInitialize() {
      if (!this.canInitializeExpress) {
        this.destroyExpressElement();
        this.loading = false;
        return;
      }

      if (this.initializing || this.expressCheckoutElement) {
        return;
      }

      this.initializing = true;
      this.loading = true;
      this.error = null;
      this.destroyExpressElement();

      await this.initialize();

      this.initializing = false;
    },

    async initialize() {
      try {
        this.$store.commit('checkout/setIsExpressActive', true);
        const data = await this.stripePayment.intent({
          paymentMethodTypes: this.paymentMethodTypes,
          isDigital: this.isDigitalCart,
          clientSecret: this.$store.state.checkout.stripeClientSecret,
        });

        this.$store.commit('checkout/setStripeClientSecret', data.ClientSecret);
        this.clientSecret = data.ClientSecret;

        if (!this.clientSecret) {
          this.loading = false;
          return;
        }

        this.elements = this.stripe.elements({
          clientSecret: this.clientSecret,
          locale: this.$i18n.locale ?? 'fr',
        });

        this.expressCheckoutElement = this.createExpressCheckoutElement();
        this.expressCheckoutElement.mount('#stripe-express-googlepay-element');

        this.expressCheckoutElement.on('ready', async ({ availablePaymentMethods }) => {
          this.isEmpty = !availablePaymentMethods || Object.values(availablePaymentMethods).every(v => v === false);
          
          // Silent catch for local environments with self-signed SSL bypassing
          if (!availablePaymentMethods?.googlePay && !availablePaymentMethods?.applePay && !availablePaymentMethods?.paypal) {
            console.warn('Payment Methods API returned false - Wallet may be forced via "always" param');
          }

          if (!this.initialContextPrepared) {
            await this.ensureInitialDeliveryAndCarrier();
            this.initialContextPrepared = true;
          }
        });

        this.expressCheckoutElement.on(
          'shippingaddresschange',
          this.handleShippingAddressChange
        );
        this.expressCheckoutElement.on(
          'shippingratechange',
          this.handleShippingRateChange
        );
        this.expressCheckoutElement.on('confirm', this.handleConfirm);
      } catch (error) {
        console.error('stripe express init error: ', error);
        this.error = error?.message || error;
      } finally {
        this.loading = false;
      }
    },

    createExpressCheckoutElement() {
      const options = {
        paymentMethodOrder: ['apple_pay', 'google_pay', 'paypal'],
        paymentMethods: {
          googlePay: 'always',
          applePay: 'auto',
          paypal: 'auto',
          link: 'never',
        },
        layout: {
          maxColumns: 2,
          maxRows: 0,
          overflow: 'never',
        },
        buttonHeight: 48,
        buttonType: {
          googlePay: 'plain',
          applePay: 'plain',
          paypal: 'paypal',
        },
        buttonTheme: {
          googlePay: 'black',
          applePay: 'black',
          paypal: 'gold',
        },
        requestPayerName: true,
        requestPayerEmail: true,
        requestShipping: !this.isDigitalCart,
        emailRequired: false,
        phoneNumberRequired: false,
        shippingAddressRequired: !this.isDigitalCart,
      };

      try {
        return this.elements.create('expressCheckout', options);
      } catch (error) {
        return this.elements.create('expressCheckout', {
          requestPayerName: true,
          requestPayerEmail: true,
          requestShipping: !this.isDigitalCart,
          emailRequired: false,
          phoneNumberRequired: false,
          shippingAddressRequired: !this.isDigitalCart,
          layout: {
            maxColumns: 2,
            maxRows: 0,
            overflow: 'never',
          },
          buttonHeight: 48,
          buttonType: {
            googlePay: 'plain',
            applePay: 'plain',
            paypal: 'paypal',
          },
          buttonTheme: {
            googlePay: 'black',
            applePay: 'black',
            paypal: 'gold',
          },
          wallets: {
            googlePay: 'always',
            applePay: 'auto',
            paypal: 'auto',
          },
        });
      }
    },

    /**
     * Detect wallet placeholder names that should NOT be persisted
     * as the customer's real identity (e.g. Google Pay test cards).
     */
    isWalletPlaceholderName(firstname, lastname) {
      if (!firstname) return true;
      const fn = (firstname || '').trim().toLowerCase();
      const ln = (lastname || '').trim().toLowerCase();
      const placeholders = ['card', 'express', 'test', 'browser', 'google', 'apple'];
      // 'Card Holder Name', 'Card Holder', 'Test User', etc.
      if (placeholders.includes(fn)) return true;
      if (fn === 'card' && ln.startsWith('holder')) return true;
      if (fn === 'test' && (ln === 'user' || ln === 'test')) return true;
      return false;
    },

    /**
     * Get the best known real identity for the current user,
     * preferring PrestaShop auth data over wallet-provided names.
     */
    getBestKnownIdentity() {
      // 1. Logged-in user from PrestaShop auth (most authoritative)
      const authUser = this.$auth?.user?.Customer;
      if (authUser?.Firstname && !this.isWalletPlaceholderName(authUser.Firstname, authUser.Lastname)) {
        return { firstname: authUser.Firstname, lastname: authUser.Lastname || '' };
      }

      // 2. Guest info from checkout store
      const guest = this.guestInfo;
      if (guest?.firstname && !this.isWalletPlaceholderName(guest.firstname, guest.lastname)) {
        return { firstname: guest.firstname, lastname: guest.lastname || '' };
      }

      // 3. Shipping address from checkout store
      const addr = this.shippingAddress;
      if (addr?.Firstname && !this.isWalletPlaceholderName(addr.Firstname, addr.Lastname)) {
        return { firstname: addr.Firstname, lastname: addr.Lastname || '' };
      }

      return null;
    },

    toCheckoutAddress(address = {}, name = '', phone = '') {
      const nameParts = (name || '').trim().split(' ').filter(Boolean);
      const walletFirstname = nameParts[0] || '';
      const walletLastname = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      // Use the best real identity, falling back to wallet name only if it's not a placeholder
      const knownIdentity = this.getBestKnownIdentity();
      let finalFirstname, finalLastname;

      if (knownIdentity) {
        // We have a real identity — always prefer it
        finalFirstname = knownIdentity.firstname;
        finalLastname = knownIdentity.lastname;
      } else if (!this.isWalletPlaceholderName(walletFirstname, walletLastname)) {
        // Wallet provided a real-looking name
        finalFirstname = walletFirstname;
        finalLastname = walletLastname;
      } else {
        // Placeholder name with no better alternative — use generic
        finalFirstname = walletFirstname || 'Client';
        finalLastname = walletLastname || 'Express';
      }

      const authAlias = this.shippingAddress?.Alias && this.shippingAddress.Alias !== 'Express checkout'
        ? this.shippingAddress.Alias
        : `${finalFirstname} ${finalLastname}`.trim();

      return {
        Firstname: finalFirstname,
        Lastname: finalLastname,
        Address1:
          address?.addressLine?.[0] ||
          address?.line1 ||
          address?.address1 ||
          this.shippingAddress?.Address1 ||
          'Adresse protégée par Google Pay',
        Address2: address?.addressLine?.[1] || address?.line2 || address?.address2 || '',
        Postcode:
          address?.postal_code ||
          address?.postalCode ||
          this.shippingAddress?.Postcode ||
          '00000',
        City: address?.city || this.shippingAddress?.City || '...',
        CountryIsoCode:
          (address?.country?.length === 2 ? address.country : null) ||
          (address?.country_code?.length === 2 ? address.country_code : null) ||
          (this.shippingAddress?.CountryIsoCode?.length === 2
            ? this.shippingAddress.CountryIsoCode
            : null) ||
          'FR',
        MobilePhone: phone || this.shippingAddress?.MobilePhone || '0600000000',
        Alias: authAlias,
        IsDelivery: true,
        IsInvoice: this.sameAsDelivery,
      };
    },

    mapCarrierGroupsToStripeRates(carrier = {}) {
      const groups = [
        ...(carrier.Store || []).map((item) => ({ ...item, _group: 'store' })),
        ...(carrier.RelayPoint || []).map((item) => ({ ...item, _group: 'relay' })),
        ...(carrier.Home || []).map((item) => ({ ...item, _group: 'home' })),
      ];

      return groups
        .filter((item) => item?.IdCarrier)
        .map((item) => {
          const amount = Math.round(Number(item?.Price?.TaxIncl || 0) * 100);
          const id = String(item.IdCarrier);
          // Use compound ID for Stripe rate to guarantee uniqueness across different amounts
          const uniqueId = `${id}_${amount}`;
          const label = item.Title || item.Name || 'Livraison';
          
          this.shippingRatesById[uniqueId] = item;

          return {
            id: uniqueId,
            label,
            displayName: label,
            amount,
          };
        });
    },

    getFallbackShippingRates() {
      const currentCarrier = this.cart?.Shipping?.Carrier;
      const amount = Math.round(Number(this.cart?.Total?.Shipping?.TaxIncl || 0) * 100);
      const fallbackId = currentCarrier?.IdCarrier
        ? `${currentCarrier.IdCarrier}_${amount}`
        : `fallback_${amount}`;
      const label = currentCarrier?.Title ||
            currentCarrier?.Name ||
            this.$t('Standard delivery');

      if (currentCarrier?.IdCarrier) {
        this.shippingRatesById[fallbackId] = currentCarrier;
      }

      return [
        {
          id: fallbackId,
          label,
          displayName: label,
          amount,
        },
      ];
    },

    async ensureInitialDeliveryAndCarrier() {
      if (this.$auth?.loggedIn) {
        try {
          await this.addressService.fetchAddresses();
        } catch (error) {
          console.error('stripe express fetch addresses error:', error);
        }
      }

      const existingCheckoutAddress = this.$store.state.checkout.shippingAddress;
      const defaultAddress =
        existingCheckoutAddress || this.getAddressDelivery || this.addresses?.[0] || null;

      if (!defaultAddress) {
        return;
      }

      this.$store.commit('checkout/setShippingAddress', { data: defaultAddress });
      if (this.sameAsDelivery) {
        this.$store.commit('checkout/setInvoiceAddress', { data: defaultAddress });
      }

      if (this.isDigitalCart) {
        return;
      }

      const countryIsoCode =
        defaultAddress?.CountryIsoCode || defaultAddress?.Country?.IsoCode;
      if (!countryIsoCode) {
        return;
      }

      let carriers = null;
      try {
        carriers = await this.withTimeout(
          this.carriersService.getCarriers({
            idAddress: defaultAddress?.IdAddress,
            idCart: this.cart?.IdCart,
            postcode: defaultAddress?.Postcode,
            city: defaultAddress?.City,
            address1: defaultAddress?.Address1 || defaultAddress?.City || 'Main Street',
            country: countryIsoCode,
            countryIsoCode,
            languageIsoCode: this.$i18n.locale,
            currencyIsoCode: this.cart?.Currency?.IsoCode,
          })
        );
      } catch (error) {
        console.error('stripe express fetch carriers error:', error);
      }

      this.shippingRatesById = {};
      const shippingRates = carriers?.Carrier
        ? this.mapCarrierGroupsToStripeRates(carriers.Carrier)
        : [];

      // Use plain carrier ID from cart for backend API
      const rawCarrierId = this.cart?.Shipping?.Carrier?.IdCarrier;
      const selectedRateId = rawCarrierId
        ? String(rawCarrierId)
        : (shippingRates?.[0] ? this.extractCarrierId(shippingRates[0].id) : null);

      if (!selectedRateId) {
        return;
      }

      this.pendingShippingRateId = shippingRates?.[0]?.id || selectedRateId;

      try {
        await this.cartService.setCarrierToCart({
          idCarrier: selectedRateId,
          countryIsoCode,
          idAddress: this.idAddressDelivery,
          idAddressInvoice: this.idAddressInvoice,
        });
        await this.cartService.fetchCart({ countryIsoCode });

        // Find matching carrier in our map by the real ID
        const selectedCarrier = Object.values(this.shippingRatesById).find(
          c => String(c.IdCarrier) === String(selectedRateId)
        );
        if (selectedCarrier) {
          this.$store.commit('checkout/setCheckoutCarrier', {
            data: selectedCarrier,
          });
        }
      } catch (error) {
        console.error('stripe express set initial carrier error:', error);
      }
    },

    async updateCheckoutAddressFromEvent(event, persistInStore = false) {
      if (this.isDigitalCart) return null;

      const shippingAddress =
        event?.address ||
        event?.shippingAddress?.address ||
        event?.billingDetails?.address ||
        event?.paymentMethod?.billing_details?.address ||
        this.pendingShippingAddress ||
        {};
      const payerName =
        event?.name || event?.payerName || event?.shippingAddress?.name || event?.billingDetails?.name || event?.paymentMethod?.billing_details?.name || this.pendingPayerName || '';
      const phone =
        event?.phone || event?.shippingAddress?.phone || event?.billingDetails?.phone || event?.paymentMethod?.billing_details?.phone || '';

      const checkoutAddress = this.toCheckoutAddress(shippingAddress, payerName, phone);

      this.pendingShippingAddress = shippingAddress;
      this.pendingPayerName = payerName;

      if (!persistInStore) {
        return checkoutAddress;
      }

      this.$store.commit('checkout/setShippingAddress', { data: checkoutAddress });
      if (this.sameAsDelivery) {
        this.$store.commit('checkout/setInvoiceAddress', { data: checkoutAddress });
      }

      return checkoutAddress;
    },

    /**
     * Update backend PaymentIntent to match the current cart total,
     * then tell the Elements instance to re-fetch the updated amount.
     */
    async updatePaymentIntent() {
      // Re-read latest cart from store to get the freshest amount
      const currentCart = this.$store.state.cart;
      const cartTotalVal = Math.round(Number(currentCart?.Total?.ToPay?.TaxIncl || 0) * 100);

      if (cartTotalVal <= 0) {
        console.warn('[Express] Skipping intent update: cart total is 0');
        return;
      }

      const data = await this.stripePayment.intent({
        paymentMethodTypes: this.paymentMethodTypes,
        isDigital: this.isDigitalCart,
        clientSecret: this.$store.state.checkout.stripeClientSecret,
        amount: cartTotalVal,
      });

      if (data && data.ClientSecret) {
        this.$store.commit('checkout/setStripeClientSecret', data.ClientSecret);
        this.clientSecret = data.ClientSecret;
      }

      // Tell the Express Checkout Element to re-fetch the PaymentIntent amount
      if (this.elements && typeof this.elements.fetchUpdates === 'function') {
        try {
          await this.elements.fetchUpdates();
        } catch (e) {
          console.warn('[Express] fetchUpdates failed:', e);
        }
      }
    },

    async syncPopupStateToStore({ rateId, checkoutAddress }) {
      if (!checkoutAddress) return;

      try {
        this.isProcessingSync = true;
        this.$store.commit('checkout/setIsExpressSyncing', true);

        // 1. Update addresses in store
        this.$store.commit('checkout/setShippingAddress', { data: checkoutAddress });
        if (this.sameAsDelivery) {
          this.$store.commit('checkout/setInvoiceAddress', { data: checkoutAddress });
        }

        // 2. Guarantee Guest Info has an email so PrestaShop accepts address creation
        if (!this.guestInfo?.email) {
          this.$store.commit('checkout/setGuestInfo', {
            data: {
              ...(this.guestInfo || {}),
              firstname: this.guestInfo?.firstname || 'Express',
              lastname: this.guestInfo?.lastname || 'Checkout',
              email: this.guestInfo?.email || 'express@checkout.com',
            },
          });
        }

        // 3. Save address in PrestaShop so cart relies on correct country for taxes & shipping
        let newIdAddress = this.idAddressDelivery;
        const addressHash = (checkoutAddress.Firstname || '') + (checkoutAddress.Address1 || '') + (checkoutAddress.Postcode || '') + (checkoutAddress.City || '');

        if (this.lastSyncedAddressHash !== addressHash) {
          try {
            const savedAddress = await this.addressService.add({
              ...checkoutAddress,
              IsDelivery: true,
              IsInvoice: true,
            });
            if (savedAddress && savedAddress.IdAddress) {
              newIdAddress = savedAddress.IdAddress;
              this.lastSyncedAddressHash = addressHash;
              await this.addressService.updateAddressType({
                idAddress: newIdAddress,
                isDelivery: true,
                isInvoice: true,
              });
              await this.addressService.fetchAddresses().catch(() => {});
            }
          } catch (e) {
            console.warn('Could not save Google Pay address dynamically', e);
          }
        }

        // 4. Set carrier using the real carrier ID (not the compound rate ID)
        const realCarrierId = this.extractCarrierId(rateId);
        if (realCarrierId && realCarrierId !== 'fallback' && this.lastSyncedRateId !== String(rateId)) {
          const carrierPayload = {
            idCarrier: realCarrierId,
            countryIsoCode: checkoutAddress.CountryIsoCode,
          };
          if (newIdAddress) {
            carrierPayload.idAddress = newIdAddress;
            carrierPayload.idAddressInvoice = newIdAddress;
          }

          await this.cartService.setCarrierToCart(carrierPayload);
          this.lastSyncedRateId = String(rateId);

          // Find matching carrier in our map
          const selectedCarrier = this.shippingRatesById[String(rateId)];
          if (selectedCarrier) {
            this.$store.commit('checkout/setCheckoutCarrier', { data: selectedCarrier });
          }
        }

        // 5. ALWAYS fetch fresh cart to ensure totals are accurate after carrier/address changes
        await this.cartService.fetchCart({
          countryIsoCode: checkoutAddress.CountryIsoCode,
        });

        await this.$nextTick();
        this.lastFetchedCartHash = addressHash + '_' + (rateId || 'none');
        
        return this.cart;
      } catch (error) {
        console.error('stripe express background sync error:', error);
        return this.cart;
      } finally {
        this.isProcessingSync = false;
        this.$store.commit('checkout/setIsExpressSyncing', false);
      }
    },

    async handleShippingAddressChange(event) {
      try {
        const checkoutAddress = await this.updateCheckoutAddressFromEvent(
          event,
          false
        );

        if (!checkoutAddress?.Postcode || !checkoutAddress?.City) {
          return event.resolve({ shippingRates: this.getFallbackShippingRates() });
        }

        this.shippingRatesById = {};
        const carriers = await this.withTimeout(
          this.carriersService.getCarriers({
            idCart: this.cart?.IdCart,
            postcode: checkoutAddress.Postcode,
            city: checkoutAddress.City,
            address1: checkoutAddress.Address1 || checkoutAddress.City || 'Main Street',
            country: checkoutAddress.CountryIsoCode,
            countryIsoCode: checkoutAddress.CountryIsoCode,
            languageIsoCode: this.$i18n.locale,
            currencyIsoCode: this.cart?.Currency?.IsoCode,
          })
        );

        if (!carriers?.Carrier) {
          return event.resolve({ shippingRates: this.getFallbackShippingRates() });
        }

        const shippingRates = this.mapCarrierGroupsToStripeRates(carriers.Carrier);
        if (!shippingRates.length) {
          return event.resolve({ shippingRates: this.getFallbackShippingRates() });
        }

        // Wait for any ongoing sync to finish
        let retries = 0;
        while (this.isProcessingSync && retries < 20) {
          await new Promise((r) => setTimeout(r, 100));
          retries++;
        }

        // Sync the first shipping rate selection + address to the backend
        await this.syncPopupStateToStore({
          checkoutAddress,
          rateId: shippingRates[0].id,
        });

        // Update the PaymentIntent with the new total, then let Stripe fetch it
        await this.updatePaymentIntent();

        console.log('[Express Address] Resolved with', shippingRates.length, 'rates');

        // Resolve with ONLY shippingRates — the total comes from the PaymentIntent
        event.resolve({ shippingRates });

      } catch (error) {
        console.error('stripe express address change error:', error);
        try {
          event.resolve({ shippingRates: this.getFallbackShippingRates() });
        } catch (e) {}
      }
    },

    async handleShippingRateChange(event) {
      try {
        const shippingRate = event?.shippingRate;
        if (!shippingRate?.id) {
          return event.resolve();
        }

        const rateId = String(shippingRate.id);
        this.pendingShippingRateId = rateId;
        const checkoutAddress = this.toCheckoutAddress(
          this.pendingShippingAddress || {},
          this.pendingPayerName || ''
        );

        // Wait for any ongoing sync to finish
        let retries = 0;
        while (this.isProcessingSync && retries < 20) {
          await new Promise((r) => setTimeout(r, 100));
          retries++;
        }

        // Sync the new carrier selection to the backend
        await this.syncPopupStateToStore({
          rateId,
          checkoutAddress,
        });

        // Update the PaymentIntent with the new total after carrier change
        await this.updatePaymentIntent();

        console.log('[Express Rate] Resolved for rate:', rateId);

        // Resolve with no lineItems — the total comes from the updated PaymentIntent
        event.resolve();
      } catch (error) {
        console.error('stripe express rate change error:', error);
        try {
          event.resolve();
        } catch (e) {}
      }
    },

    async postData() {
      try {
        await this.stripePayment.postData({
          clientSecret: this.clientSecret,
          paymentMethod: this.paymentMethod,
        });
        return true;
      } catch (error) {
        return false;
      }
    },

    async handleConfirm(event) {
      if (this.submitLoading) return;
      this.submitLoading = true;
      this.toggleLoader(true);

      try {
        console.log('[Express Confirm] Full event payload:', JSON.parse(JSON.stringify(event)));

        const freshEventAddress = await this.updateCheckoutAddressFromEvent(
          event,
          true
        );

        const email =
          event?.email ||
          event?.payerEmail ||
          event?.billingDetails?.email ||
          event?.paymentMethod?.billing_details?.email ||
          this.guestInfo?.email ||
          '';

        // Filter out wallet placeholder names (Card Holder, Test User, etc.)
        // regardless of login status — always prefer real identity
        const knownIdentity = this.getBestKnownIdentity();
        let finalFirstname, finalLastname;

        if (this.isWalletPlaceholderName(freshEventAddress.Firstname, freshEventAddress.Lastname) && knownIdentity) {
          // Wallet gave us a placeholder — use the real identity we already know
          finalFirstname = knownIdentity.firstname;
          finalLastname = knownIdentity.lastname;
        } else if (!this.isWalletPlaceholderName(freshEventAddress.Firstname, freshEventAddress.Lastname)) {
          // Wallet gave a real-looking name — use it (this is the unredacted confirm data)
          finalFirstname = freshEventAddress.Firstname;
          finalLastname = freshEventAddress.Lastname;
        } else {
          // No identity at all — fallback
          finalFirstname = freshEventAddress.Firstname || 'Client';
          finalLastname = freshEventAddress.Lastname || 'Express';
        }

        // 1. Initial Guest registration if completely new user
        if (!this.isLoggedIn && email) {
          this.$store.commit('checkout/setGuestInfo', {
            data: {
              firstname: finalFirstname,
              lastname: finalLastname,
              email,
            },
          });
          await this.ensureAddressesSaved().catch(() => {});
        }

        // 2. Save the final unredacted address from Google Pay to backend
        let finalIdAddress = this.idAddressDelivery;
        try {
          if (finalIdAddress) {
            await this.addressService.update(finalIdAddress, {
              ...freshEventAddress,
              Firstname: finalFirstname,
              Lastname: finalLastname,
              IsDelivery: true,
              IsInvoice: true,
            });
          } else {
            const finalAddress = await this.addressService.add({
              ...freshEventAddress,
              IsDelivery: true,
              IsInvoice: true,
            });
            if (finalAddress && finalAddress.IdAddress) {
              finalIdAddress = finalAddress.IdAddress;
              await this.addressService.updateAddressType({
                idAddress: finalIdAddress,
                isDelivery: true,
                isInvoice: true,
              });
            }
          }
        } catch (e) {
          console.warn('Could not save real address on confirm', e);
        }

        const selectedRateId = this.pendingShippingRateId || event?.shippingRate?.id;
        if (
          !this.isDigitalCart &&
          selectedRateId &&
          this.extractCarrierId(selectedRateId) !== 'fallback'
        ) {
          const realCarrierId = this.extractCarrierId(selectedRateId);
          await this.cartService.setCarrierToCart({
            idCarrier: realCarrierId,
            countryIsoCode: freshEventAddress?.CountryIsoCode,
            idAddress: finalIdAddress || this.idAddressDelivery,
            idAddressInvoice: finalIdAddress || this.idAddressInvoice,
          });
          await this.cartService.fetchCart({
            countryIsoCode: freshEventAddress?.CountryIsoCode,
          });

          const selectedCarrier = Object.values(this.shippingRatesById).find(
            c => String(c.IdCarrier) === String(realCarrierId)
          );
          if (selectedCarrier) {
            this.$store.commit('checkout/setCheckoutCarrier', {
              data: selectedCarrier,
            });
          }
        }

        // Final intent update to ensure the amount matches the cart before confirm
        await this.updatePaymentIntent();

        const posted = await this.postData();
        if (!posted) {
          throw new Error(this.$t('Unable to initialize payment data.'));
        }

        const idorder = this.cart.IdCart;
        const { error, paymentIntent } = await this.stripe.confirmPayment({
          elements: this.elements,
          clientSecret: this.clientSecret,
          confirmParams: {
            return_url: `${process.env.URL}cart/thanks/accepted?orderid=${idorder}&cartid=${this.cartService.idCart}`,
          },
          redirect: 'if_required',
        });

        if (error) {
          this.showMessage(error.message);
          return;
        }

        if (paymentIntent && paymentIntent.status === 'succeeded') {
          this.addPaymentInfoGtm('google_pay');
          this.$router.push({
            path: this.localePath(
              `/cart/thanks/accepted?orderid=${idorder}&cartid=${this.cartService.idCart}`
            ),
          });
        }
      } catch (error) {
        console.error('stripe express confirm error:', error);
        this.showMessage(error?.message || this.$t('Payment failed.'));
      } finally {
        this.submitLoading = false;
        this.toggleLoader(false);
      }
    },

    showMessage(messageText) {
      if (!process.client) return;

      const messageContainer = document.querySelector(
        '#stripe-express-googlepay-message'
      );
      if (!messageContainer) return;

      messageContainer.classList.remove('hidden');
      messageContainer.textContent = messageText;

      setTimeout(() => {
        messageContainer.classList.add('hidden');
        messageContainer.textContent = '';
      }, 4000);
    },

    toggleLoader(isLoading) {
      this.$store.commit('shared/wind/setIsLoading', isLoading);
    },
  },
};
